import * as Sentry from '@sentry/react';

/**
 * Initializes Sentry Error Tracking if a DSN is available.
 * Operates silently if VITE_SENTRY_DSN is not configured.
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  
  if (!dsn) {
    console.info('[Sentry] No VITE_SENTRY_DSN configured. Error tracking running in silent local mode.');
    return;
  }

  Sentry.init({
    dsn,
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    // Performance Monitoring
    tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0,
    // Session Replays / Error tracking
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    environment: import.meta.env.MODE || 'development',
    // Ignore benign transient browser errors
    ignoreErrors: [
      'Failed to update a ServiceWorker',
      'Failed to register a ServiceWorker',
      'An unknown error occurred when fetching the script',
      'Failed to fetch dynamically imported module',
      'Load failed',
      'Loading chunk',
      'NetworkError when attempting to fetch resource',
      'The operation was aborted',
      /Lock ".*" was released because another request stole it/,
      'was released because another request stole it',
    ],
    beforeSend(event, hint) {
      const error = hint?.originalException;
      const message =
        (error && typeof error === 'object' && 'message' in error)
          ? String((error as any).message)
          : String(error || event.message || '');

      // Suppress transient ServiceWorker network update errors and Supabase multi-tab auth lock contention
      if (
        message.includes('Failed to update a ServiceWorker') ||
        message.includes('Failed to register a ServiceWorker') ||
        message.includes('An unknown error occurred when fetching the script') ||
        message.includes('was released because another request stole it')
      ) {
        return null;
      }

      return event;
    },
  });

  console.info('[Sentry] Error tracking initialized successfully.');
}

/**
 * Sets the active user context in Sentry for detailed error tracking.
 */
export function setSentryUser(user: { id?: string; email?: string; familyId?: string; role?: string } | null) {
  if (!user) {
    Sentry.setUser(null);
    return;
  }

  Sentry.setUser({
    id: user.id,
    email: user.email,
  });

  if (user.familyId || user.role) {
    Sentry.setTags({
      family_id: user.familyId || 'none',
      user_role: user.role || 'member',
    });
  }
}

/**
 * Manually captures an exception with optional extra context tags.
 */
export function captureException(error: unknown, context?: Record<string, any>) {
  if (context) {
    Sentry.withScope((scope) => {
      scope.setExtras(context);
      Sentry.captureException(error);
    });
  } else {
    Sentry.captureException(error);
  }
}

/**
 * Manually logs a custom message or warning to Sentry.
 */
export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info', context?: Record<string, any>) {
  if (context) {
    Sentry.withScope((scope) => {
      scope.setExtras(context);
      Sentry.captureMessage(message, level);
    });
  } else {
    Sentry.captureMessage(message, level);
  }
}
